import { createSelector } from "reselect";

export const selectSensorData = createSelector(
  (state) => state?.liveData || {},
  (_, ship) => ship, // Ship parameter
  (_, __, sensorValue) => sensorValue, // Sensor value parameter
  (liveData, ship, sensorValue) =>
    liveData?.[ship]?.data?.latestRecord?.[sensorValue] ?? null, // Return only the sensor value, or null if not found
);

export const selectAlarmData = createSelector(
  (state) => state?.alarm?.alarms || {}, // Provide a default empty object if alarms is undefined
  (_, key) => key,
  (alarms, key) => alarms[key],
);

export const selectAlarmDataTag = createSelector(
  (state, ship) => state.liveData?.[ship]?.data?.latestRecord || null,
  (_, __, prefixes) => prefixes,
  (latestRecord, prefixes) => {
    if (!latestRecord) return {};
    return Object.fromEntries(
      Object.entries(latestRecord).filter(
        ([key, val]) =>
          !key.startsWith("-") &&
          !key.endsWith("RAW") &&
          (!key.endsWith("ACT") || key.endsWith("AN_ACT")) &&
          prefixes.some((prefix) => {
            if (prefix.includes("*")) {
              const [start, end] = prefix.split("*");
              return key.startsWith(start) && key.endsWith(end);
            }
            return key.startsWith(prefix);
          }),
      ),
    );
  },
);

export const selectRawAndActValues = (state, ship) => {
  const latestRecord = state.liveData?.[ship]?.data?.latestRecord || null;
  if (!latestRecord) return {};

  return Object.fromEntries(
    Object.entries(latestRecord)
      .filter(
        ([key]) =>
          key.endsWith("RAW") || key.endsWith("ACT") || key.endsWith("OFFSET"),
      )
      .map(([key, value]) => {
        let label = "";
        if (key.includes("TANKS")) {
          label += "TANKS ";
        }
        if (key.includes("ANOUT")) {
          label += "ANOUT_";
        }
        if (key.endsWith("RAW")) {
          label += "RAW";
        } else if (key.endsWith("ACT")) {
          label += "ACT";
        } else if (key.endsWith("OFFSET")) {
          label += "OFFSET";
        }
        return [label, value];
      }),
  );
};

// Selector for multiple sensor values with formatting options
export const selectMultipleSensorData = createSelector(
  (state) => state.liveData,
  (_, sensorValues) => sensorValues,
  (_, __, decimals) => decimals,
  (liveData, sensorValues, decimals) =>
    sensorValues.reduce((acc, sensorValue) => {
      let value = liveData[sensorValue];
      if (value !== undefined && value !== null) {
        acc[sensorValue] =
          decimals !== undefined ? parseFloat(value).toFixed(decimals) : value;
      } else {
        acc[sensorValue] = value;
      }
      return acc;
    }, {}),
);
